import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { 
  getLoggedInParticipant, 
  logoutParticipant
} from '../api/participantAuth';
import { useParticipants } from '../hooks/useParticipants';
import PersonalInfoTab from '../components/PersonalInfoTab';
import HandlerTab from '../components/HandlerTab';
import BreederTab from '../components/BreederTab';
import AdminTab from '../components/AdminTab';
import EditControls from '../components/EditControls';

export default function ModernDashboard() {
  const location = useLocation();
  const [tab, setTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = parseInt(params.get('tab'), 10);
    return !isNaN(tabParam) ? tabParam : 0;
  });
  const [isEditing, setIsEditing] = useState(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = parseInt(params.get('tab'), 10);
    return (!isNaN(tabParam) ? tabParam : 0) === 0;
  });
    // Keep tab state in sync with URL
    useEffect(() => {
      const params = new URLSearchParams(location.search);
      const tabParam = parseInt(params.get('tab'), 10);
      if (!isNaN(tabParam) && tab !== tabParam) {
        setTab(tabParam);
      }
    }, [location.search]);

    // Update URL when tab changes
    const handleTabChange = (newTab) => {
      setTab(newTab);
      setIsEditing(newTab === 0);
      const params = new URLSearchParams(location.search);
      params.set('tab', newTab);
      history.replace({ search: params.toString() });
    };
  const [saving, setSaving] = useState(false);
  const history = useHistory();
  const { participants: allParticipants, loadParticipants } = useParticipants();
  const [participant, setParticipant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [showWelcome, setShowWelcome] = useState(true);

  const hasRole = (roleToCheck) => {
    if (!participant) return false;
    if (Array.isArray(participant.role)) {
      return participant.role.some(r => r.toLowerCase() === roleToCheck.toLowerCase());
    }
    return participant.role?.toLowerCase() === roleToCheck.toLowerCase();
  };



  useEffect(() => {
    const authData = getLoggedInParticipant();

    if (!authData || !authData.participantId) {
      history.push('/race/login');
    } else {
      loadParticipantData(authData.participantId);
    }
  }, [history]);





  const loadParticipantData = async (participantId) => {
    try {
      setLoading(true);
      // Force reload participants and get the fresh list directly
      const freshParticipants = await loadParticipants(true);
      
      const current = freshParticipants.find(p => p.id === participantId);
      
      if (current) {
        setParticipant(current);
      } else {
        setMessage('Unable to load your profile. Please try logging in again.');
        setTimeout(() => {
          history.push('/race/participants');
        }, 3000);
      }
    } catch (error) {
      setMessage('Failed to load profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleParticipantUpdate = async () => {
    const authData = getLoggedInParticipant();
    if (authData?.participantId) {
      await loadParticipantData(authData.participantId);
    }
  };

  const handleLogout = () => {
    logoutParticipant();
    history.push('/race/participants');
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      // Check which tab is currently active based on the actual tab index
      // Let each tab handle its own saving state and validation
      if (tab === 0 && window.personalInfoTabSave) {
        await window.personalInfoTabSave();
      } else if (tab === 1 && hasRole('handler') && window.handlerTabSave) {
        await window.handlerTabSave();
      } else if (tab === 2 && hasRole('breeder') && window.breederTabSave) {
        await window.breederTabSave();
      } else if (tab === 3 && hasRole('admin') && window.adminTabSave) {
        await window.adminTabSave();
      }
    } catch (error) {
      // Individual tabs handle their own saving state cleanup
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setMessage('');
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!participant) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          Participant data not found. Please try logging in again.
        </div>
      </div>
    );
  }

  if (showWelcome) {
    return (
      <div className="container py-5">
        <div className="card shadow-sm my-5 mx-auto" style={{ maxWidth: 500 }}>
          <div className="card-body text-center">
            <h4 className="mb-4">Welcome, {participant?.fullName || 'Member'}!</h4>
            <p className="mb-4">What would you like to do?</p>
            <div className="d-grid gap-3">
              <button className="btn btn-primary" onClick={() => { setShowWelcome(false); setTab(0); }}>
                Update My Info
              </button>
              {hasRole('breeder') && (
                <button className="btn btn-success" onClick={() => { setShowWelcome(false); setTab(2); }}>
                  View My Handlers
                </button>
              )}
              {hasRole('handler') && (
                <button className="btn btn-info" onClick={() => { setShowWelcome(false); setTab(1); }}>
                  View Requests from Breeders
                </button>
              )}
              {hasRole('admin') && (
                <button className="btn btn-warning" onClick={() => { setShowWelcome(false); setTab(3); }}>
                  Admin Panel
                </button>
              )}
            </div>
          </div>
        </div>
        {/* Quick Actions on Welcome Dashboard */}
        <div className="card shadow-sm my-4 mx-auto" style={{ maxWidth: 500, borderRadius: '0', borderLeft: 'none', borderRight: 'none' }}>
          <div className="card-header bg-white py-3">
            <h5 className="mb-0" style={{ color: '#0b3d91' }}>
              <i className="bi bi-lightning me-2"></i>
              Quick Actions
            </h5>
          </div>
          <div className="card-body px-2 px-md-3">
            <div className="d-flex flex-column flex-md-row gap-3">
              <button 
                className="btn btn-outline-primary w-100 w-md-auto"
                style={{ minHeight: '44px' }}
                onClick={() => history.push('/race/participants')}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Back to Participants List
              </button>
              <button 
                className="btn btn-outline-primary w-100 w-md-auto"
                style={{ minHeight: '44px' }}
                onClick={() => history.push('/if-convention-2026')}
              >
                <i className="bi bi-info-circle me-2"></i>
                Race Details
              </button>
              <button 
                className="btn btn-outline-secondary w-100 w-md-auto"
                style={{ minHeight: '44px' }}
                onClick={handleLogout}
              >
                <i className="bi bi-box-arrow-right me-2"></i>
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-0 py-3 py-md-5">
      <div className="container px-0 px-md-3">
        {/* Exit to Welcome Dashboard Button (Top) - REMOVED as per new instruction */}
        {/* Hide Tabs and Tab Content when showWelcome is true */}
        {!showWelcome && (
          <>
            <div className="card shadow mb-4 mx-0 mx-md-auto" style={{ borderRadius: '0', borderLeft: 'none', borderRight: 'none' }}>
              <div className="card-header bg-white">
                <div>Welcome to Member Dashboard <b><i className="bi bi-house-door-fill">{participant.fullName}</i></b>!</div>
              </div>
              <div className="card-body px-2 px-md-3">
                {message && (
                  <div className={`alert ${message.includes('success') ? 'alert-success' : 'alert-info'} mb-3 mx-2 mx-md-0`}>
                    {message}
                  </div>
                )}
                {/* Exit to Welcome Dashboard Button (Top of Tab) */}
                <div className="d-flex justify-content-end mb-3">
                  <button
                    className="btn btn-danger btn-lg fw-bold px-4 py-2 shadow-sm border-3 border-dark"
                    style={{ zIndex: 10 }}
                    onClick={() => setShowWelcome(true)}
                  >
                    <i className="bi bi-arrow-bar-left me-2"></i>
                    Exit to Welcome Dashboard
                  </button>
                </div>
                {/* Edit Controls - Visible across all tabs */}
                {(tab === 0 || (tab === 3 && hasRole('admin'))) && (
                  <div className="ms-4">
                    <EditControls
                      isEditing={isEditing}
                      saving={saving}
                      onEdit={handleEdit}
                      onSave={handleSave}
                      onCancel={handleCancel}
                    />
                  </div>
                )}
                <div className="p-2 p-md-3 bg-light rounded shadow-sm mx-2 mx-md-0">
                  {tab === 0 && (
                    <PersonalInfoTab
                      participant={participant}
                      onMessage={setMessage}
                      onParticipantUpdate={handleParticipantUpdate}
                      isEditing={true}
                      onEditingChange={setIsEditing}
                      saving={saving}
                      setSaving={setSaving}
                      onTabSwitch={setTab}
                    />
                  )}
                  {tab === 1 && hasRole('handler') && (
                    <HandlerTab
                      participant={participant}
                      onMessage={setMessage}
                      onParticipantUpdate={handleParticipantUpdate}
                      isEditing={isEditing}
                      onEditingChange={setIsEditing}
                      saving={saving}
                      setSaving={setSaving}
                    />
                  )}
                  {tab === 2 && hasRole('breeder') && (
                    <BreederTab
                      participant={participant}
                      onMessage={setMessage}
                      onParticipantUpdate={handleParticipantUpdate}
                      isEditing={isEditing}
                      onEditingChange={setIsEditing}
                      saving={saving}
                      setSaving={setSaving}
                    />
                  )}
                  {tab === 3 && hasRole('admin') && (
                    <AdminTab
                      participant={participant}
                      onMessage={setMessage}
                      onParticipantUpdate={handleParticipantUpdate}
                      isEditing={isEditing}
                      onEditingChange={setIsEditing}
                      saving={saving}
                      setSaving={setSaving}
                    />
                  )}
                  {/* Exit to Welcome Dashboard Button (Bottom of Tab) */}
                  <div className="d-flex justify-content-end mt-4 mb-2">
                    <button
                      className="btn btn-danger btn-lg fw-bold px-4 py-2 shadow-sm border-3 border-dark"
                      style={{ zIndex: 10 }}
                      onClick={() => setShowWelcome(true)}
                    >
                      <i className="bi bi-arrow-bar-left me-2"></i>
                      Exit to Welcome Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {/* Quick Actions */}
            <div className="card shadow-sm mx-0 mx-md-auto" style={{ borderRadius: '0', borderLeft: 'none', borderRight: 'none' }}>
              <div className="card-header bg-white py-3">
                <h5 className="mb-0" style={{ color: '#0b3d91' }}>
                  <i className="bi bi-lightning me-2"></i>
                  Quick Actions
                </h5>
              </div>
              <div className="card-body px-2 px-md-3">
                <div className="d-flex flex-column flex-md-row gap-3">
                  <button 
                    className="btn btn-outline-primary w-100 w-md-auto"
                    style={{ minHeight: '44px' }}
                    onClick={() => history.push('/race/participants')}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Back to Participants List
                  </button>
                  <button 
                    className="btn btn-outline-primary w-100 w-md-auto"
                    style={{ minHeight: '44px' }}
                    onClick={() => history.push('/if-convention-2026')}
                  >
                    <i className="bi bi-info-circle me-2"></i>
                    Race Details
                  </button>
                  <button 
                    className="btn btn-outline-secondary w-100 w-md-auto"
                    style={{ minHeight: '44px' }}
                    onClick={handleLogout}
                  >
                    <i className="bi bi-box-arrow-right me-2"></i>
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
